import { FC, useState } from 'react';
import { createStyles, makeStyles } from '@material-ui/core';
import { Typography, TextField } from '@material-ui/core';

import {
  getMonitoringErrorMessage,
  loginMonitoring,
} from 'src/apis/monitoring';
import Button from 'src/components/Button';
import AuthLayout from 'src/containers/auth/AuthLayout';
import { useCommonStyles } from 'src/containers/onboarding-redesign/utils/useCommonStyles';

const useStyles = makeStyles(() =>
  createStyles({
    input: {
      width: '100%',
      marginTop: 52,
      '& .MuiInputBase-input': {
        fontWeight: 400,
        fontSize: 20,
        lineHeight: '32px',
        color: '#545454',

        '@media screen and (min-width: 768px)': {
          fontSize: 25,
          lineHeight: '40px',
        },
      },

      '& .MuiInput-underline:before': {
        borderBottomColor: '#545454',
      },
    },
    error: {
      marginTop: 12,
      color: '#CB1223',
      fontSize: 14,
      lineHeight: '20px',
    },
    submitBtn: {
      background:
        'linear-gradient(259.81deg, #E671A9 -4.16%, #9873CB 49.59%, #7570DF 84.28%, #736FD5 126.47%)',
      borderRadius: 2,
      padding: '6px 16px',
      height: 40,
      marginTop: 52,
      minWidth: 140,

      '&:hover': {
        background:
          'linear-gradient(259.81deg, #E671A9 -4.16%, #9873CB 49.59%, #7570DF 84.28%, #736FD5 126.47%)',
      },

      '& > .MuiButton-label': {
        display: 'flex',
        alignItems: 'center',
        gap: 45,
        fontSize: 16,
        lineHeight: '24px',
        color: '#EFEDEB',
        fontWeight: 500,
      },
    },
  })
);

interface MetricsPasswordGateProps {
  onUnlock: () => void;
}

const MetricsPasswordGate: FC<MetricsPasswordGateProps> = ({ onUnlock }) => {
  const classes = useStyles();
  const commonStyles = useCommonStyles();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await loginMonitoring(password);
      onUnlock();
    } catch (err) {
      setError(getMonitoringErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit}>
        <Typography className={commonStyles.headline1}>Metrics</Typography>
        <Typography className={commonStyles.body1}>
          Enter the internal password to access the health and error dashboard.
        </Typography>
        <TextField
          id='metrics-password'
          type='password'
          placeholder='Password'
          className={classes.input}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) {
              setError('');
            }
          }}
        />
        {error && <Typography className={classes.error}>{error}</Typography>}
        <div>
          <Button
            className={classes.submitBtn}
            color='primary'
            type='submit'
            disabled={!password || isSubmitting}
          >
            <span>{isSubmitting ? 'Signing in…' : 'Continue'}</span>
            <span>→</span>
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default MetricsPasswordGate;
